import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fin } from "@/lib/api";
import { toast } from "sonner";
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, ArrowLeft, LogOut,
  Landmark, Receipt, FileBarChart2, AlertCircle, Plus, Calendar,
  ArrowUpRight, ArrowDownRight, Percent, Target,
} from "lucide-react";

/**
 * Financeiro & Contábil — Central Executiva
 * Conformidade: SPED, EFD, DCTFWeb, PIS/COFINS/ISS/ICMS/IRPJ/CSLL
 */
export default function Financeiro() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [contas, setContas] = useState([]);
  const [tributos, setTributos] = useState([]);
  const [fluxo, setFluxo] = useState([]);
  const [filterTipo, setFilterTipo] = useState("");
  const [showNovaConta, setShowNovaConta] = useState(false);

  useEffect(() => { load(); }, [tab, filterTipo]);

  const load = async () => {
    try {
      if (tab === "dashboard") {
        const [d, f] = await Promise.all([fin.dashboard(), fin.fluxoCaixa()]);
        setDash(d.data);
        setFluxo(f.data);
      } else if (tab === "contas") {
        const r = await fin.contas(filterTipo ? { tipo: filterTipo } : {});
        setContas(r.data);
      } else if (tab === "tributos") {
        const r = await fin.tributos();
        setTributos(r.data);
      } else if (tab === "fluxo") {
        const r = await fin.fluxoCaixa();
        setFluxo(r.data);
      }
    } catch { toast.error("Erro ao carregar"); }
  };

  const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="min-h-screen bg-[#0A0B1A] flex flex-col">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/25 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center">
            <Wallet size={20} className="text-[#B28F4B]" />
          </div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold font-heading text-lg">SOEASY · Financeiro & Contábil</div>
            <div className="text-white/60 text-[10px] uppercase tracking-widest">Central Executiva · SPED · EFD · DCTFWeb · Conformidade Tributária</div>
          </div>
          <div className="hidden md:flex gap-2">
            <span className="px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold uppercase tracking-widest">SPED Fiscal</span>
            <span className="px-2.5 py-1 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 text-[9px] font-bold uppercase tracking-widest">EFD Contribuições</span>
            <span className="px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 text-[9px] font-bold uppercase tracking-widest">Simples/Lucro Real</span>
          </div>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white text-xs font-bold uppercase tracking-widest">Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">Sair</button>
        </div>
        {/* Tabs */}
        <div className="max-w-[1600px] mx-auto px-5 flex gap-1 overflow-x-auto">
          {[
            { id: "dashboard", label: "Central Executiva", icon: FileBarChart2 },
            { id: "contas", label: "Contas a Pagar/Receber", icon: Receipt },
            { id: "tributos", label: "Tributos & Obrigações", icon: Percent },
            { id: "fluxo", label: "Fluxo de Caixa", icon: TrendingUp },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-fin-${t.id}`}
              className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest transition border-b-2 flex items-center gap-1.5 ${
                tab === t.id ? "border-[#B28F4B] text-[#B28F4B]" : "border-transparent text-white/60 hover:text-white"
              }`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto p-5 fade-up">
        {tab === "dashboard" && dash && <DashFin dash={dash} fluxo={fluxo} fmt={fmt} />}
        {tab === "contas" && (
          <Contas contas={contas} fmt={fmt} filterTipo={filterTipo} setFilterTipo={setFilterTipo} onNovo={() => setShowNovaConta(true)} showNova={showNovaConta} setShowNova={setShowNovaConta} reload={load} />
        )}
        {tab === "tributos" && <Tributos tributos={tributos} fmt={fmt} />}
        {tab === "fluxo" && <FluxoCaixa fluxo={fluxo} fmt={fmt} />}
      </main>
    </div>
  );
}

/* ---------- Dashboard Central ---------- */
function DashFin({ dash, fluxo, fmt }) {
  const cats = Object.entries(dash.despesas_por_categoria || {}).sort((a,b) => b[1] - a[1]);
  const totalCat = cats.reduce((s, [,v]) => s + v, 0);
  return (
    <div className="space-y-5">
      {/* KPIs primários */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi title="A Receber (aberto)" value={fmt(dash.receber_aberto)} icon={ArrowUpRight} color="#10B981" bg="from-emerald-500/10 to-transparent"/>
        <Kpi title="A Pagar (aberto)" value={fmt(dash.pagar_aberto)} icon={ArrowDownRight} color="#EF4444" bg="from-red-500/10 to-transparent"/>
        <Kpi title="Saldo do Mês" value={fmt(dash.saldo_mes)} icon={DollarSign} color={dash.saldo_mes >= 0 ? "#B28F4B" : "#EF4444"} bg="from-[#B28F4B]/10 to-transparent"/>
        <Kpi title="Vencidas" value={dash.vencidas} icon={AlertCircle} color="#F59E0B" bg="from-amber-500/10 to-transparent" sub="títulos"/>
      </div>

      {/* DRE simplificado + Tributos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em]">DRE Simplificada</div>
              <div className="text-white font-heading text-lg font-bold">Mês Corrente</div>
            </div>
            <FileBarChart2 size={22} className="text-[#B28F4B]"/>
          </div>
          <div className="space-y-3">
            <DreRow label="Receitas Operacionais" value={fmt(dash.receitas_mes)} positive/>
            <DreRow label="( − ) Despesas Operacionais" value={fmt(dash.despesas_mes)} negative/>
            <div className="h-px bg-[#B28F4B]/30" />
            <DreRow label="Lucro Bruto" value={fmt(dash.lucro_bruto)} bold accent="#B28F4B"/>
            <div className="flex items-center justify-between p-3 bg-[#B28F4B]/10 rounded-lg mt-3">
              <div className="text-[#B28F4B] text-xs font-bold uppercase tracking-widest">Margem</div>
              <div className="text-[#D4AF37] font-mono font-bold text-lg">{dash.margem_pct}%</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em]">Obrigações Tributárias</div>
              <div className="text-white font-heading text-lg font-bold">Conformidade Fiscal</div>
            </div>
            <Landmark size={22} className="text-[#B28F4B]"/>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="text-amber-400 text-[10px] font-bold uppercase tracking-widest">A Recolher</div>
              <div className="text-amber-300 font-mono font-bold text-lg mt-1">{fmt(dash.tributos_a_recolher)}</div>
            </div>
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="text-red-400 text-[10px] font-bold uppercase tracking-widest">Atrasados</div>
              <div className="text-red-300 font-mono font-bold text-lg mt-1">{fmt(dash.tributos_atrasados)}</div>
            </div>
          </div>
          <div className="text-[#A1A4C7] text-[10px] leading-relaxed">
            Impostos monitorados: <span className="text-white font-mono">PIS · COFINS · ISS · ICMS · IRPJ · CSLL · INSS · FGTS · IRRF · DAS</span>
          </div>
        </div>
      </div>

      {/* Categorias de despesa */}
      <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
        <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Despesas Pagas por Categoria (mês)</div>
        <div className="space-y-2">
          {cats.length === 0 && <div className="text-[#A1A4C7] text-xs">Nenhuma despesa paga neste mês.</div>}
          {cats.map(([cat, val]) => (
            <div key={cat}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-white font-medium uppercase tracking-wide">{cat.replace(/_/g, " ")}</span>
                <span className="text-[#B28F4B] font-mono font-bold">{fmt(val)}</span>
              </div>
              <div className="h-1.5 bg-[#0A0B1A] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#B28F4B] to-[#D4AF37]" style={{ width: `${(val/totalCat*100).toFixed(1)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fluxo mini */}
      <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl p-5">
        <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Fluxo de Caixa Projetado (próximos 30 dias)</div>
        <div className="overflow-x-auto">
          <div className="flex gap-1 items-end h-32 min-w-full">
            {fluxo.slice(7, 37).map(f => {
              const max = Math.max(...fluxo.slice(7,37).map(x => Math.max(x.entrada, x.saida)), 1);
              return (
                <div key={f.data} className="flex-1 flex flex-col items-center gap-0.5 min-w-[16px]">
                  <div className="w-full flex flex-col gap-0.5">
                    <div className="w-full bg-emerald-500/60 rounded-t" style={{ height: `${f.entrada/max*80}px` }} title={`Entrada ${fmt(f.entrada)}`} />
                    <div className="w-full bg-red-500/60 rounded-b" style={{ height: `${f.saida/max*80}px` }} title={`Saída ${fmt(f.saida)}`} />
                  </div>
                  <div className="text-[8px] text-[#6B6E8F] font-mono">{f.data.slice(8)}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-4 mt-3 text-[10px]">
          <span className="flex items-center gap-1 text-emerald-400"><span className="w-2 h-2 bg-emerald-500 rounded-sm"/>Entradas</span>
          <span className="flex items-center gap-1 text-red-400"><span className="w-2 h-2 bg-red-500 rounded-sm"/>Saídas</span>
        </div>
      </div>
    </div>
  );
}

function Kpi({ title, value, icon: Icon, color, bg, sub }) {
  return (
    <div className={`bg-gradient-to-br ${bg} border border-[#B28F4B]/25 rounded-2xl p-4 backdrop-blur`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#A1A4C7]">{title}</div>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="font-mono font-bold text-xl" style={{ color }}>{value}</div>
      {sub && <div className="text-[9px] text-[#6B6E8F] mt-0.5 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}

function DreRow({ label, value, positive, negative, bold, accent }) {
  return (
    <div className="flex justify-between text-sm">
      <span className={`${bold ? "font-bold" : ""} text-white/80`}>{label}</span>
      <span className="font-mono font-bold" style={{ color: accent || (positive ? "#10B981" : negative ? "#EF4444" : "#fff") }}>{value}</span>
    </div>
  );
}

/* ---------- Contas a Pagar/Receber ---------- */
function Contas({ contas, fmt, filterTipo, setFilterTipo, onNovo, showNova, setShowNova, reload }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-1">
          {[["", "Todas"], ["receber", "A Receber"], ["pagar", "A Pagar"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilterTipo(v)}
              data-testid={`filter-conta-${v || "todas"}`}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${
                filterTipo === v ? "bg-[#B28F4B] text-[#1A1D42] border-[#B28F4B]" : "bg-transparent text-white/60 border-[#B28F4B]/30 hover:border-[#B28F4B]/60"
              }`}
            >{l}</button>
          ))}
        </div>
        <button onClick={onNovo} data-testid="btn-nova-conta" className="px-4 py-2 rounded-lg bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5">
          <Plus size={13}/> Nova Conta
        </button>
      </div>

      {showNova && <NovaContaModal onClose={() => setShowNova(false)} onSaved={reload} />}

      <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25">
            <tr className="text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]">
              <th className="p-3">Tipo</th>
              <th className="p-3">Descrição</th>
              <th className="p-3">Contraparte</th>
              <th className="p-3">Categoria</th>
              <th className="p-3 text-right">Valor</th>
              <th className="p-3">Vencimento</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {contas.map(c => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${c.tipo === "receber" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                    {c.tipo === "receber" ? "↑ Receber" : "↓ Pagar"}
                  </span>
                </td>
                <td className="p-3 text-white text-xs">{c.descricao}</td>
                <td className="p-3 text-[#A1A4C7] text-xs">{c.fornecedor_cliente}</td>
                <td className="p-3 text-[#A1A4C7] text-[10px] uppercase">{c.categoria?.replace(/_/g, " ")}</td>
                <td className="p-3 text-right font-mono text-white font-bold">{fmt(c.valor)}</td>
                <td className="p-3 text-[#A1A4C7] text-xs font-mono">{c.data_vencimento}</td>
                <td className="p-3">
                  <StatusBadge status={c.status} />
                </td>
              </tr>
            ))}
            {contas.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#A1A4C7] text-xs">Nenhuma conta encontrada.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    aberta: { c: "#F59E0B", l: "Aberta" },
    paga: { c: "#10B981", l: "Paga" },
    recebida: { c: "#10B981", l: "Recebida" },
    vencida: { c: "#EF4444", l: "Vencida" },
    cancelada: { c: "#6B7280", l: "Cancelada" },
    a_recolher: { c: "#F59E0B", l: "A Recolher" },
    recolhido: { c: "#10B981", l: "Recolhido" },
    atrasado: { c: "#EF4444", l: "Atrasado" },
  };
  const m = map[status] || { c: "#6B7280", l: status };
  return <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest" style={{ background: `${m.c}20`, color: m.c }}>{m.l}</span>;
}

function NovaContaModal({ onClose, onSaved }) {
  const [f, setF] = useState({ tipo: "pagar", descricao: "", fornecedor_cliente: "", valor: 0, data_vencimento: "", categoria: "fornecedores", forma_pagamento: "boleto" });
  const [saving, setSaving] = useState(false);
  const salvar = async () => {
    if (!f.descricao || !f.fornecedor_cliente || !f.data_vencimento) return toast.error("Preencha os campos obrigatórios");
    setSaving(true);
    try {
      await fin.criarConta({ ...f, valor: Number(f.valor) });
      toast.success("Conta criada");
      onClose(); onSaved();
    } catch { toast.error("Erro ao criar"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[#131532] border border-[#B28F4B]/40 rounded-2xl p-6 w-full max-w-2xl">
        <h3 className="text-[#B28F4B] font-heading text-lg font-bold mb-4">Nova Conta</h3>
        <div className="grid grid-cols-2 gap-3">
          <select value={f.tipo} onChange={e => setF({...f, tipo: e.target.value})} className="col-span-2 px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
            <option value="pagar">Conta a Pagar</option><option value="receber">Conta a Receber</option>
          </select>
          <input placeholder="Descrição" value={f.descricao} onChange={e => setF({...f, descricao: e.target.value})} className="col-span-2 px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <input placeholder="Fornecedor/Cliente" value={f.fornecedor_cliente} onChange={e => setF({...f, fornecedor_cliente: e.target.value})} className="col-span-2 px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <input type="number" step="0.01" placeholder="Valor" value={f.valor} onChange={e => setF({...f, valor: e.target.value})} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <input type="date" value={f.data_vencimento} onChange={e => setF({...f, data_vencimento: e.target.value})} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm"/>
          <select value={f.categoria} onChange={e => setF({...f, categoria: e.target.value})} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
            <option value="fornecedores">Fornecedores</option><option value="salarios">Salários</option><option value="impostos">Impostos</option><option value="aluguel">Aluguel</option><option value="servicos">Serviços</option><option value="servicos_prestados">Serviços Prestados</option>
          </select>
          <select value={f.forma_pagamento} onChange={e => setF({...f, forma_pagamento: e.target.value})} className="px-3 py-2 rounded-lg bg-[#0A0B1A] border border-[#B28F4B]/25 text-white text-sm">
            <option value="boleto">Boleto</option><option value="pix">PIX</option><option value="transferencia">Transferência</option><option value="cartao">Cartão</option><option value="dinheiro">Dinheiro</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/20 text-white/60 text-xs font-bold uppercase tracking-widest">Cancelar</button>
          <button onClick={salvar} disabled={saving} className="px-4 py-2 rounded-lg bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] text-xs font-bold uppercase tracking-widest disabled:opacity-50">{saving ? "Salvando..." : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Tributos ---------- */
function Tributos({ tributos, fmt }) {
  const total = tributos.reduce((s, t) => s + t.valor, 0);
  const aRec = tributos.filter(t => t.status === "a_recolher").reduce((s, t) => s + t.valor, 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi title="Total Competência" value={fmt(total)} icon={Landmark} color="#B28F4B" bg="from-[#B28F4B]/10 to-transparent"/>
        <Kpi title="A Recolher" value={fmt(aRec)} icon={AlertCircle} color="#F59E0B" bg="from-amber-500/10 to-transparent"/>
        <Kpi title="Recolhidos" value={fmt(total - aRec)} icon={Target} color="#10B981" bg="from-emerald-500/10 to-transparent"/>
      </div>
      <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]">
            <tr><th className="p-3">Tributo</th><th className="p-3">Competência</th><th className="p-3 text-right">Base Cálculo</th><th className="p-3 text-right">Alíquota</th><th className="p-3 text-right">Valor</th><th className="p-3">Vencimento</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {tributos.map(t => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="p-3"><span className="text-[#B28F4B] font-mono font-bold">{t.tributo}</span></td>
                <td className="p-3 text-white text-xs font-mono">{t.competencia}</td>
                <td className="p-3 text-right text-white/70 text-xs font-mono">{fmt(t.base_calculo)}</td>
                <td className="p-3 text-right text-white/70 text-xs font-mono">{t.aliquota}%</td>
                <td className="p-3 text-right font-mono text-white font-bold">{fmt(t.valor)}</td>
                <td className="p-3 text-[#A1A4C7] text-xs font-mono">{t.vencimento}</td>
                <td className="p-3"><StatusBadge status={t.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Fluxo de Caixa ---------- */
function FluxoCaixa({ fluxo, fmt }) {
  return (
    <div className="bg-gradient-to-br from-[#131532] to-[#0A0B1A] border border-[#B28F4B]/25 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-[#B28F4B]/25">
        <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em]">Fluxo de Caixa — 37 dias (7 passados + 30 futuros)</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#0A0B1A]/60 border-b border-[#B28F4B]/25 text-left text-[9px] font-bold uppercase tracking-[0.2em] text-[#B28F4B]">
          <tr><th className="p-3">Data</th><th className="p-3 text-right">Entradas</th><th className="p-3 text-right">Saídas</th><th className="p-3 text-right">Saldo Dia</th><th className="p-3 text-right">Saldo Acum.</th></tr>
        </thead>
        <tbody>
          {fluxo.map(f => (
            <tr key={f.data} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="p-3 text-white text-xs font-mono">{f.data}</td>
              <td className="p-3 text-right text-emerald-400 font-mono">{fmt(f.entrada)}</td>
              <td className="p-3 text-right text-red-400 font-mono">{fmt(f.saida)}</td>
              <td className="p-3 text-right font-mono font-bold" style={{ color: f.saldo_dia >= 0 ? "#10B981" : "#EF4444" }}>{fmt(f.saldo_dia)}</td>
              <td className="p-3 text-right font-mono font-bold" style={{ color: f.saldo >= 0 ? "#B28F4B" : "#EF4444" }}>{fmt(f.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
